/* tslint:disable */
import { Injectable } from '@angular/core';
import { HttpResponse, HttpContext, HttpParams } from '@angular/common/http';
import { WorkflowBaseService } from '../workflow-base-service';
import { StrictHttpResponse } from '../strict-http-response';
import { RequestBuilder } from '../request-builder';
import { Observable } from 'rxjs';
import { map, filter } from 'rxjs/operators';

import { BulkExecuteActionRequestDto } from '../models/bulk-execute-action-request-dto';
import { BulkExecuteWorkflowActionResponseDto } from '../models/bulk-execute-workflow-action-response-dto';
import { ChangeStepOwnerDto } from '../models/change-step-owner-dto';
import { ChangeWorkFlowStepDto } from '../models/change-work-flow-step-dto';
import { CreateAdhocWorkflowStepDto } from '../models/create-adhoc-workflow-step-dto';
import { EntryMainWorkflowStatusDto } from '../models/entry-main-workflow-status-dto';
import { ExecuteActionModel } from '../models/execute-action-model';
import { ReferencePropertyWithResolved } from '../models/reference-property-with-resolved';

@Injectable({ providedIn: 'root' })
export class WorkflowControllerService extends WorkflowBaseService {

  /**
   * Path part for operation workflowControllerGetMainWorkflowStatus
   */
  static readonly WorkflowControllerGetMainWorkflowStatusPath = '/v1/entities/{entity}/entries/{entryId}/main-flow-status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMainWorkflowStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainWorkflowStatus$Response(params: {
    entity: string;
    entryId: string;
    mode?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<EntryMainWorkflowStatusDto>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerGetMainWorkflowStatusPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('entryId', params.entryId, {});
      rb.query('mode', params.mode, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EntryMainWorkflowStatusDto>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMainWorkflowStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainWorkflowStatus(params: {
    entity: string;
    entryId: string;
    mode?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<EntryMainWorkflowStatusDto> {

    return this.getMainWorkflowStatus$Response(params).pipe(
      map((r: StrictHttpResponse<EntryMainWorkflowStatusDto>) => r.body as EntryMainWorkflowStatusDto)
    );
  }

  /**
   * Path part for operation workflowControllerChangeCurrentStep
   */
  static readonly WorkflowControllerChangeCurrentStepPath = '/v1/entities/{entity}/entries/{entry}/workflow/{workflowId}/change-step';

  /**
   * Change current step of a data entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changeCurrentStep()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeCurrentStep$Response(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ChangeWorkFlowStepDto
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerChangeCurrentStepPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('workflowId', params.workflowId, {});
      rb.path('entry', params.entry, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Change current step of a data entry
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `changeCurrentStep$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeCurrentStep(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ChangeWorkFlowStepDto
  }): Observable<any> {

    return this.changeCurrentStep$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation workflowControllerAddAdHocStep
   */
  static readonly WorkflowControllerAddAdHocStepPath = '/v1/entities/{entity}/entries/{entry}/{workflowId}/adhoc-step';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `addAdHocStep()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addAdHocStep$Response(params: {
    entity: string;
    entry: string;
    workflowId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: CreateAdhocWorkflowStepDto
  }): Observable<StrictHttpResponse<void>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerAddAdHocStepPath, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('entry', params.entry, {});
      rb.path('workflowId', params.workflowId, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'text',
      accept: '*/*',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return (r as HttpResponse<any>).clone({ body: undefined }) as StrictHttpResponse<void>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `addAdHocStep$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  addAdHocStep(params: {
    entity: string;
    entry: string;
    workflowId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: CreateAdhocWorkflowStepDto
  }): Observable<void> {

    return this.addAdHocStep$Response(params).pipe(
      map((r: StrictHttpResponse<void>) => r.body as void)
    );
  }

  /**
   * Path part for operation workflowControllerExecuteWorkflowAction
   */
  static readonly WorkflowControllerExecuteWorkflowActionPath = '/v1/entities/{entity}/entries/{entry}/{workflowId}/execute-action';

  /**
   * Execute action of workflow that belongs to an entity
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `executeWorkflowAction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  executeWorkflowAction$Response(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ExecuteActionModel
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerExecuteWorkflowActionPath, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('workflowId', params.workflowId, {});
      rb.path('entry', params.entry, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Execute action of workflow that belongs to an entity
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `executeWorkflowAction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  executeWorkflowAction(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ExecuteActionModel
  }): Observable<any> {

    return this.executeWorkflowAction$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

  /**
   * Path part for operation workflowControllerGetWorkFlowOwners
   */
  static readonly WorkflowControllerGetWorkFlowOwnersPath = '/v1/entities/{entity}/workflow/{scope}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getWorkFlowOwners()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkFlowOwners$Response(params: {
    entity: string;
    scope: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<Array<ReferencePropertyWithResolved>>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerGetWorkFlowOwnersPath, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('scope', params.scope, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<Array<ReferencePropertyWithResolved>>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getWorkFlowOwners$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getWorkFlowOwners(params: {
    entity: string;
    scope: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<Array<ReferencePropertyWithResolved>> {

    return this.getWorkFlowOwners$Response(params).pipe(
      map((r: StrictHttpResponse<Array<ReferencePropertyWithResolved>>) => r.body as Array<ReferencePropertyWithResolved>)
    );
  }

  /**
   * Path part for operation workflowControllerBulkExecuteWorkflowAction
   */
  static readonly WorkflowControllerBulkExecuteWorkflowActionPath = '/v1/entities/{entity}/{workflowId}/{actionId}/bulk';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `bulkExecuteWorkflowAction()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkExecuteWorkflowAction$Response(params: {
    entity: string;
    workflowId: string;
    actionId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkExecuteActionRequestDto
  }): Observable<StrictHttpResponse<BulkExecuteWorkflowActionResponseDto>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerBulkExecuteWorkflowActionPath, 'post');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('workflowId', params.workflowId, {});
      rb.path('actionId', params.actionId, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<BulkExecuteWorkflowActionResponseDto>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `bulkExecuteWorkflowAction$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  bulkExecuteWorkflowAction(params: {
    entity: string;
    workflowId: string;
    actionId: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: BulkExecuteActionRequestDto
  }): Observable<BulkExecuteWorkflowActionResponseDto> {

    return this.bulkExecuteWorkflowAction$Response(params).pipe(
      map((r: StrictHttpResponse<BulkExecuteWorkflowActionResponseDto>) => r.body as BulkExecuteWorkflowActionResponseDto)
    );
  }

  /**
   * Path part for operation workflowControllerGetMainWorkflowStatusV2
   */
  static readonly WorkflowControllerGetMainWorkflowStatusV2Path = '/v2/entities/{entity}/entries/{entryId}/main-flow-status';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getMainWorkflowStatusV2()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainWorkflowStatusV2$Response(params: {
    entity: string;
    entryId: string;
    mode?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<StrictHttpResponse<EntryMainWorkflowStatusDto>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerGetMainWorkflowStatusV2Path, 'get');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('entryId', params.entryId, {});
      rb.query('mode', params.mode, {});
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<EntryMainWorkflowStatusDto>;
      })
    );
  }
  /**
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `getMainWorkflowStatusV2$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getMainWorkflowStatusV2(params: {
    entity: string;
    entryId: string;
    mode?: string;
    extraParams?: HttpParams,
    context?: HttpContext
  }): Observable<EntryMainWorkflowStatusDto> {

    return this.getMainWorkflowStatusV2$Response(params).pipe(
      map((r: StrictHttpResponse<EntryMainWorkflowStatusDto>) => r.body as EntryMainWorkflowStatusDto)
    );
  }

  /**
   * Path part for operation workflowControllerChangeCurrentStepOwnerV2
   */
  static readonly WorkflowControllerChangeCurrentStepOwnerV2Path = '/v2/entities/{entity}/entries/{entry}/{workflowId}/change-owner';

  /**
   * Change the workflow owner of a data entry
   *
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `changeCurrentStepOwnerV2()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeCurrentStepOwnerV2$Response(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ChangeStepOwnerDto
  }): Observable<StrictHttpResponse<any>> {

    const rb = new RequestBuilder(this.rootUrl, WorkflowControllerService.WorkflowControllerChangeCurrentStepOwnerV2Path, 'patch');
    if (params) {
      rb.path('entity', params.entity, {});
      rb.path('workflowId', params.workflowId, {});
      rb.path('entry', params.entry, {});
      rb.body(params.body, 'application/json');
      rb.extraHttpParams(params.extraParams);
    }

    return this.http.request(rb.build({
      responseType: 'json',
      accept: 'application/json',
      context: params?.context
    })).pipe(
      filter((r: any) => r instanceof HttpResponse),
      map((r: HttpResponse<any>) => {
        return r as StrictHttpResponse<any>;
      })
    );
  }
  /**
   * Change the workflow owner of a data entry
   *
   * This method provides access to only to the response body.
   * To access the full response (for headers, for example), `changeCurrentStepOwnerV2$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  changeCurrentStepOwnerV2(params: {
    entity: string;
    workflowId: string;
    entry: string;
    extraParams?: HttpParams,
    context?: HttpContext
    body?: ChangeStepOwnerDto
  }): Observable<any> {

    return this.changeCurrentStepOwnerV2$Response(params).pipe(
      map((r: StrictHttpResponse<any>) => r.body as any)
    );
  }

}
